<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Tender } from '@/types/tender'
import TextareaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'

interface Props {
  tender: Tender | null
  tenders: Tender[]
}

interface Emits {
  (e: 'reject', data: { tenderId: string | string[]; comment: string }): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const rejectionComment = ref('')
const commentValidationError = ref('')

const isBulkRejection = computed(() => props.tenders.length > 0)

// Validation function
const validateComment = () => {
  if (!rejectionComment.value.trim()) {
    commentValidationError.value = 'Please provide a reason for rejection'
    return false
  }
  if (rejectionComment.value.trim().length < 10) {
    commentValidationError.value = 'Rejection reason must be at least 10 characters long'
    return false
  }
  commentValidationError.value = ''
  return true
}

// Handle comment change
const handleCommentChange = (value: string) => {
  rejectionComment.value = value
  // Clear error when user types enough characters
  if (value.trim().length >= 10) {
    commentValidationError.value = ''
  } else if (value.trim().length > 0) {
    commentValidationError.value = 'Rejection reason must be at least 10 characters long'
  }
}

// Form validation - exposed to parent
const validateForm = () => {
  return validateComment()
}

// Submit form - called by parent
const submitForm = () => {
  const isValid = validateComment()

  if (!isValid) {
    return false
  }

  if (isBulkRejection.value) {
    const tenderIds = props.tenders.map(t => t.t247Id)
    emit('reject', {
      tenderId: tenderIds,
      comment: rejectionComment.value.trim()
    })
  } else if (props.tender) {
    emit('reject', {
      tenderId: props.tender.t247Id,
      comment: rejectionComment.value.trim()
    })
  }

  return true
}

// Expose methods to parent
defineExpose({
  validateForm,
  submitForm
})
</script>

<template>
  <div class="space-y-4">
    <!-- Warning Message -->
    <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-yellow-700">
            <span class="font-medium">Warning:</span>
            This action cannot be undone. {{ isBulkRejection ? 'The selected tenders will' : 'This tender will' }} be marked as rejected.
          </p>
        </div>
      </div>
    </div>

    <!-- Tender Info (for single rejection) -->
    <div v-if="!isBulkRejection && tender" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Tender Details:</h3>
      <div class="space-y-1 text-sm">
        <div class="flex justify-between">
          <span class="text-gray-600">Reference No:</span>
          <span class="font-medium">{{ tender.referenceNo || 'N/A' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Description:</span>
          <span class="font-medium text-right">{{ tender.tenderBrief?.substring(0, 50) }}...</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Organization:</span>
          <span class="font-medium">{{ tender.organization || 'N/A' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Estimated Cost:</span>
          <span class="font-medium">₹{{ tender.estimatedCost?.toLocaleString('en-IN') }}</span>
        </div>
      </div>
    </div>

    <!-- Bulk Rejection Info -->
    <div v-if="isBulkRejection" class="bg-gray-50 p-4 rounded-lg">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-gray-700">Rejecting {{ tenders.length }} Tenders:</h3>
        <span class="text-xs px-2 py-1 rounded bg-red-100 text-red-800">
          Bulk Action
        </span>
      </div>
      <ul class="space-y-1 text-sm max-h-40 overflow-y-auto">
        <li v-for="tender in tenders" :key="tender.t247Id" class="flex justify-between py-1 border-b border-gray-200 last:border-0">
          <span class="text-gray-600">{{ tender.referenceNo }}</span>
          <span class="font-medium">{{ tender.tenderBrief?.substring(0, 30) }}...</span>
        </li>
      </ul>
    </div>

    <!-- Comment Input with TextareaDefault -->
    <div class="space-y-2">
      <label for="rejectionComment" class="block text-sm font-medium text-gray-700">
        Reason for Rejection <span class="text-red-500">*</span>
      </label>
      <TextareaDefault
        v-model="rejectionComment"
        id="rejectionComment"
        name="rejectionComment"
        placeholder="Please provide the reason for rejecting this tender..."
        :rows="4"
        :errorMessage="commentValidationError"
        @update:modelValue="handleCommentChange"
        parentClass="w-full"
      />
      <p class="text-xs text-gray-500">
        Minimum 10 characters. This comment will be stored with the rejection record for future reference.
      </p>
    </div>

    <div class="bg-blue-50 p-3 rounded-lg">
      <p class="text-xs text-blue-700">
        <span class="font-medium">Note:</span> Tenders that expiring today will not appear in the “Rejected Tender” dashboard and will be directly removed from the system upon rejection.
      </p>
    </div>
  </div>
</template>
